import { createContext, type PropsWithChildren, useContext, useMemo } from 'react';

import { type BaseCustomField } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useGetPublicCustomFields } from '~/swr-hooks/customFields/useGetPublicCustomFields';
import { isDevOrTestStage } from '~/swr-hooks/utils';

export type PublicCustomFieldsProviderProps = {
  canViewCustomFields: boolean;
} & PropsWithChildren<object>;

export type PublicCustomFieldsProviderContextValue = {
  customFields: BaseCustomField[] | undefined;
  isReady: boolean;
  error?: unknown;
};

const defaultValue: PublicCustomFieldsProviderContextValue = {
  customFields: undefined,
  isReady: false,
};

const PublicCustomFieldsProviderContext = createContext<PublicCustomFieldsProviderContextValue>(defaultValue);

export const PublicCustomFieldsProvider = ({ children, canViewCustomFields }: PublicCustomFieldsProviderProps) => {
  const { data: customFields, error, isFetched } = useGetPublicCustomFields(canViewCustomFields);
  const isReady = !canViewCustomFields || isFetched;

  const value = useMemo(
    () => ({ customFields: customFields?.data, error, isReady }),
    [customFields?.data, error, isReady],
  );

  return (
    <PublicCustomFieldsProviderContext.Provider value={value}>{children}</PublicCustomFieldsProviderContext.Provider>
  );
};

export const usePublicCustomFieldsContext = () => {
  const context = useContext(PublicCustomFieldsProviderContext);

  if (context === defaultValue) {
    const error = 'PublicCustomFieldsProviderContext used outside of PublicCustomFieldsProvider';

    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({
        error,
        context: error,
      });
    }
  }

  return context;
};
